if (!customElements.get('back-in-stock')) {
  customElements.define(
    'back-in-stock',
    // eslint-disable-next-line no-undef
    class BackInStock extends window.BAO.CustomElement() {
      constructor () {
        super()

        this.outOfStockItems = new Map()
        this.productHandles = new Map()

        this.setupListeners()
      }

      connectedCallback () {
        this.parseProductsData()
      }

      setupListeners () {
        super.setupListeners()

        this.initTagalysWidget = this.initTagalysWidget.bind(this)
        this.beforeAPICall = this.beforeAPICall.bind(this)
        this.onSuccessfulAPICall = this.onSuccessfulAPICall.bind(this)
        this.onFailedAPICall = this.onFailedAPICall.bind(this)
        this.onVariantChange = this.onVariantChange.bind(this)
        this.onDrawerOpened = this.onDrawerOpened.bind(this)
        this.handleSubmit = this.handleSubmit.bind(this)

        if (!this.hasEl('form')) return

        this.listeners.add(this.getEl('form'), 'submit', this.handleSubmit)

        window.BAO.eventBus.addEventListener(
          window.BAO.EVENTS.VARIANT.CHANGE,
          this.onVariantChange,
        )

        window.BAO.eventBus.addEventListener(
          window.BAO.EVENTS.DRAWER.OPENED,
          this.onDrawerOpened,
        )
      }

      parseProductsData () {
        try {
          const productsData = JSON.parse(this.dataset.products || '[]')
          productsData.forEach(item => {
            if (item.id && item.handle) {
              this.productHandles.set(item.id, item.handle)
            }
          })
        } catch (error) {
          console.error('Failed to parse products data:', error)
        }
      }

      onDrawerOpened (e) {
        const key = e.detail.key

        if (key !== 'bis') return

        this.resetForm()

        setTimeout(() => {
          this.initTagalysWidget()
        }, 100)
      }

      resetForm () {
        this.toggleForm(true)
        this.toggleResponse(false)
      }

      async onVariantChange (e) {
        const variantData = e.detail.variant?.data
        if (!variantData) return

        const productId = String(variantData.product_id)
        const variantId = variantData.id
        const isAvailable = variantData.available

        // Get product handle from stored map
        const handle = this.productHandles.get(productId)
        if (!handle) {
          console.warn('Product handle not found for product:', productId)
          return
        }

        const variantSize = variantData.option1 || ''

        if (isAvailable) {
          // Variant is available, remove from out-of-stock tracking
          this.outOfStockItems.delete(productId)
        } else {
          // Variant is out of stock, add/update in tracking
          this.outOfStockItems.set(productId, {
            productId,
            variantId,
            handle,
            variantSize,
          })
        }

        // Update dataset.variantSize for Tagalys widget (use last selected size)
        this.dataset.variantSize = variantSize

        // Re-render all product cards based on current state
        await this.renderProductCards()
      }

      initTagalysWidget () {
        // Only show Tagalys if we have a variant size and out-of-stock items
        if (!this.dataset.variantSize || this.outOfStockItems.size === 0) {
          return
        }

        const carouselSelector = this.externalEls?.carousel?.selector
        const tagalysWidgetId = this.externalEls?.carousel?.element.dataset
          .tagalysWidgetId
        if (!tagalysWidgetId) return

        // Use first product ID for Tagalys
        const firstItem = this.outOfStockItems.values().next().value
        const productId =
          firstItem?.productId || this.productHandles.keys().next().value

        window.Tagalys.UIWidgets.Recommendations.init(carouselSelector, {
          recommendationId: tagalysWidgetId,
          productId,
          templates: {
            widget: {
              options: {
                includeTagalysCSS: false,
                selector: carouselSelector,
                request: {},
                hierarchySeparator: '➜',
                position: false,
                minimumCharactersToShowSuggestions: 2,
                maxRecentSearchesCount: 5,
                maxTotalSearchesCount: 10,
                alignToSelector: false,
                inheritAlignmentElementWidth: false,
              },
              render: () => {},
            },
            product: {
              render: () => {},
            },
          },
          callbacks: {
            beforeAPICall: this.beforeAPICall,
            onSuccessfulAPICall: this.onSuccessfulAPICall,
            onFailedAPICall: this.onFailedAPICall,
          },
        })
      }

      beforeAPICall (params) {
        // Adding variant size scope for the BIS tagalys widget.
        // Ref: https://tagalys.notion.site/UI-Widgets-Example-snippet-to-scope-recommendation-results-1fbacdd38c208055bf2dc959be817f90
        return {
          ...params,
          scope: {
            Size: [this.dataset.variantSize],
          },
        }
      }

      onSuccessfulAPICall (response) {
        if (!this.isValidTagalysResponse(response)) {
          this.externalEls.recs.element.setAttribute('aria-hidden', true)
          return response
        }

        if (response.products?.length > 0) {
          this.renderCustomProducts(response.products)
        } else {
          // Hide the component if no products are available
          this.externalEls.recs.element.setAttribute('aria-hidden', true)
        }

        return response
      }

      onFailedAPICall (error) {
        return error
      }

      // During testing, I found that sometimes Tagalys would return products that were
      // not available in the target size.
      // This is an additional layer of filtering to ensure that we only show products
      // that are available in the target size.
      filterProductsByAvailableSize (products) {
        if (!products || !this.dataset.variantSize) {
          return products
        }

        const targetSize = this.dataset.variantSize

        return products.filter(product => {
          // Check if the product has any available variants in the target size
          return product.variants?.some(
            variant => variant.available && variant.option1 === targetSize,
          )
        })
      }

      renderCustomProducts (products) {
        const recommendationsContainer = this.externalEls?.carousel?.element

        if (!recommendationsContainer) {
          return
        }

        recommendationsContainer.innerHTML = ''

        if (products.length <= 4) {
          this.classList.add('tag-Recommendations-nokeenslider')
        }

        const productsHTML = this.renderAllProducts(products)

        const carouselHTML = `
          <tagalys-push-cart-upsell-carousel class="car-Cards">
            <div
              class="car-Cards_Items keen-slider"
              data-tagalys-push-cart-upsell-carousel-el="slides"
            >
              ${productsHTML}
            </div>
          </tagalys-push-cart-upsell-carousel>
        `

        recommendationsContainer.innerHTML = carouselHTML
      }

      renderAllProducts (products) {
        const productArray = []

        products.forEach(product => {
          productArray.push(this.renderProduct(product))
        })

        return productArray.join('')
      }

      renderProduct (product) {
        if (!this.hasRequiredProductData(product)) {
          return ''
        }

        // Get the selected variant for the target size
        const selectedVariantId = this.getSelectedVariantId(product)
        const selectedVariant = selectedVariantId
          ? product.variants?.find(v => v.id === selectedVariantId)
          : null

        return `
          <div
            class="car-Cards_Item keen-slider__slide"
            data-tagalys-push-cart-upsell-carousel-el="slide"
          >
            <article class="prd-Card util-FauxLink">
              ${this.renderProductImages(product)}

              <div class="prd-Card_Body fz-15_21">
                ${this.renderProductDetails(product)}
                ${this.renderProductColors(product)}
                ${this.renderProductForm(product, selectedVariant)}
              </div>

              ${this.renderProductLink(product)}
            </article>
          </div>
        `
      }

      renderProductForm (product, selectedVariant) {
        const hasSelectedVariant =
          !!selectedVariant && selectedVariant.available
        const hasOneOption = this.productHasOneOption(product)
        const buttonDisabled =
          !product.has_only_default_variant &&
          !hasOneOption &&
          !hasSelectedVariant

        return `
          <product-form class="prd-Card_ProductForm">
            <form
              class="prd-Card_Form"
              method="post"
              action="/cart/add"
              accept-charset="UTF-8"
              enctype="multipart/form-data"
              data-product-handle="${product.handle}"
              data-push-cart-form=""
              data-product-form=""
              data-tagalys-product-form=""
              data-variants="${encodeURIComponent(
                JSON.stringify(product.variants),
              )}"
              onchange="window.BAO.Tagalys.handleProductCardFormChange(this)"
            >
              ${this.renderFormOptions(product, selectedVariant, hasOneOption)}
              ${this.renderAddToCartButton(
                product,
                selectedVariant,
                hasOneOption,
                buttonDisabled,
              )}
              ${this.renderFormHiddenInputs(product, selectedVariant)}
            </form>
          </product-form>
        `
      }

      productHasOneOption (product) {
        return product?.options_with_values?.[0]?.values?.length === 1
      }

      renderOptionSelectors (product, selectedVariant, hasOneOption) {
        if (!product?.options_with_values?.length) return ''

        return product.options_with_values
          .map(option => {
            return `
              <div class="prd-Card_Option">
                <select
                  class="frm-Form_Select fz-13_19 prd-Card_ProductFormSelect"
                  name="options[${option.name}]"
                  data-name="${option.name}"
                  data-option-select=""
                  data-product-form-el="select"
                >
                  <option value="" disabled selected>
                    ${window.theme.strings.select}
                  </option>

                  ${option.values
                    .map(value => {
                      // Find the variant that matches this option value
                      const associatedVariant = product?.variants?.find(
                        variant => variant?.title === value,
                      )
                      const isSoldOut = associatedVariant?.available === false

                      return `
                        <option value="${value}" ${isSoldOut ? 'disabled' : ''}>
                          ${value} ${
                        isSoldOut ? `(${window.theme.strings.soldOut})` : ''
                      }
                        </option>
                      `
                    })
                    .join('')}
                </select>
              </div>
            `
          })
          .join('')
      }

      getSelectedVariantId (product) {
        const currentSize = this.dataset.variantSize
        if (!currentSize || !product.variants) {
          return null
        }

        // Find the variant that matches the current size and is available
        const matchingVariant = product.variants.find(
          variant => variant.option1 === currentSize && variant.available,
        )

        return matchingVariant?.id || null
      }

      // Product rendering helper methods
      renderSecondImage (product) {
        if (!this.hasSecondImage(product)) return ''
        return `
          <div class="prd-Card_Image prd-Card_Image-hover">
            <img
              class="product-image"
              src="${product.images[1].src}"
              alt="${product.title}"
            />
          </div>
        `
      }

      renderSaleSticker (product) {
        if (!this.isOnSale(product)) return ''
        return `<p class="prd-Card_Sticker prd-Card_Sticker-sale fz-13_17">${window.theme.strings.stickerSale}</p>`
      }

      renderProductImages (product) {
        return `
          <div class="prd-Card_ImageContainer">
            <div class="prd-Card_Image">
              <img
                class="product-image"
                src="${product.__tagalys_fields.image_url}"
                alt="${product.title}"
              />
            </div>
            ${this.renderSecondImage(product)}
            ${this.renderSaleSticker(product)}
          </div>
        `
      }

      renderProductDetails (product) {
        const productTitle = this.getProductTitle(product)
        const productPrice = this.formatProductPrice(product.price)
        const isOnSale = product.compare_at_price_max > product.price
        const compareAtPrice = this.formatProductPrice(product.compare_at_price)

        return `
          <div class="prd-Card_Details">
            <h3 class="prd-Card_Title" id="ProductCard-${product.id}">
              ${productTitle}
            </h3>

            <p class="prd-Card_Price">
              ${
                isOnSale
                  ? `
                <s class="prd-Card_RegularPrice">
                  <span class="util-ScreenReaderOnly">
                    ${window.theme.strings.price.regularPrice}
                  </span>
                  ${
                    product.compare_at_price > product.price
                      ? compareAtPrice
                      : ''
                  }
                </s>

                <span class="prd-Card_SalePrice">
                  ${productPrice}
                </span>
              `
                  : productPrice
              }
            </p>
          </div>
        `
      }

      renderProductColors (product) {
        if (!product.metafields?.colours?.title?.value?.[0]) return ''
        return `
          <div class="prd-Card_Colours">
            <p class="prd-Card_Colour prd-Card_Colour-current">
              ${product.metafields.colours.title.value[0]}
            </p>
          </div>
        `
      }

      renderProductLink (product) {
        return `
          <a href="${product.__tagalys_fields.link}" class="util-FauxLink_Link">
            <span class="util-ScreenReaderOnly">
              ${this.dataset.linkToTranslation || 'View'} ${product.title}
            </span>
          </a>
        `
      }

      // Product form helper methods
      renderFormOptions (product, selectedVariant, hasOneOption) {
        if (product.has_only_default_variant || hasOneOption) {
          return ''
        }

        return `
          <div class="prd-Card_Options">
            ${this.renderOptionSelectors(
              product,
              selectedVariant,
              hasOneOption,
            )}
          </div>
        `
      }

      renderAddToCartButton (
        product,
        selectedVariant,
        hasOneOption,
        buttonDisabled,
      ) {
        const buttonContent = product.has_only_default_variant
          ? this.renderDefaultVariantButtonContent(product)
          : this.renderMultiVariantButtonContent(product, hasOneOption)

        return `
          <button
            type="submit"
            name="add"
            class="prd-Card_Button bis-BackInStock_AtcButton btn-Button btn-Button-secondary btn-Button-fullWidth"
            data-product-add-to-cart=""
            data-product-form-el="addToCart"
            aria-hidden="false"
            ${buttonDisabled ? 'disabled' : ''}
          >
            ${buttonContent}
          </button>
        `
      }

      renderDefaultVariantButtonContent (product) {
        return `
          <span>
            <span class="prd-Card_ButtonText" data-product-add-to-cart-text="add" aria-hidden="${!product
              .variants?.[0]?.available}">
              ${window.theme.strings.addToCart}
            </span>

            <span class="prd-Card_ButtonText" data-product-add-to-cart-text="soldout" aria-hidden="${
              product.variants?.[0]?.available
            }">
              ${window.theme.strings.soldOut}
            </span>
          </span>
        `
      }

      renderMultiVariantButtonContent (product, hasOneOption) {
        return `
          <span>
            <span class="prd-Card_ButtonText" data-product-add-to-cart-text="add" aria-hidden="false">
              ${window.theme.strings.addToCart}
              ${
                product.has_only_default_variant || hasOneOption
                  ? ''
                  : `<svg class="prd-Card_ButtonTextIcon" viewBox="0 0 5 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m.68 6.506 3.005-3.004L.68.5" stroke="currentColor" stroke-linecap="round"></path></svg>`
              }
            </span>

            <span class="prd-Card_ButtonText" data-product-add-to-cart-text="soldout" aria-hidden="true">
              ${window.theme.strings.soldOut}
            </span>
          </span>
        `
      }

      renderFormHiddenInputs (product, selectedVariant) {
        return `
          <input type="hidden" name="id" data-variant-id value="${selectedVariant?.id ||
            ''}">
          <input type="hidden" name="form_type" value="product">
          <input type="hidden" name="utf8" value="✓">
          <input type="hidden" name="attributes[last_added_product_id]" value="${
            product.id
          }">
        `
      }

      // Utility methods for data transformations
      hasSecondImage (product) {
        return 1 in product.images
      }

      isOnSale (product) {
        return (
          product.compare_at_price !== product.price &&
          product.compare_at_price !== null
        )
      }

      getProductTitle (product) {
        return product.metafields?.details?.clean_title?.value || product.title
      }

      formatProductPrice (price) {
        if (price === 0) return ''
        return window.BAO.Utils.formatMoney(price * 100, theme.moneyFormat)
      }

      findVariantBySize (product, targetSize) {
        return product.variants?.find(
          variant => variant.option1 === targetSize && variant.available,
        )
      }

      // Validation methods
      hasRequiredProductData (product) {
        return product && product.variants && product.handle
      }

      hasValidVariantSize (variantSize) {
        return typeof variantSize === 'string' && variantSize.trim().length > 0
      }

      isValidTagalysResponse (response) {
        return response && Array.isArray(response.products)
      }

      async renderProductCards () {
        const productsContainer = this.getEl('products')
        if (!productsContainer) return

        // If no out-of-stock items, clear container
        if (this.outOfStockItems.size === 0) {
          productsContainer.innerHTML = ''
          return
        }

        // Render all out-of-stock product cards
        const productCardPromises = Array.from(
          this.outOfStockItems.values(),
        ).map(item => this.renderSingleProductCard(item))

        const productCards = await Promise.all(productCardPromises)

        // Filter out nulls and join HTML
        const validCards = productCards.filter(card => card !== null)
        productsContainer.innerHTML = validCards
          .map(card => card.outerHTML)
          .join('')
      }

      async renderSingleProductCard (item) {
        const { handle, variantId } = item

        const res = await window.BAO.sectionRenderer.fetchSingle(
          'product-card-bis',
          `/products/${handle}?variant=${variantId}`,
        )

        const newDOM = await window.BAO.sectionRenderer.convertToDOM(res)
        const productCardEl = newDOM.querySelector('#product-card-bis')
        if (!productCardEl) return null

        return productCardEl
      }

      async handleSubmit (event) {
        event.preventDefault()
        if (this.email.length < 1) return

        // Submit BIS for all out-of-stock items
        const bisResponses = []
        for (const item of this.outOfStockItems.values()) {
          try {
            const response = await this.sendBIS(item.productId, item.variantId)
            bisResponses.push({ success: true, response, item })
          } catch (error) {
            bisResponses.push({ success: false, error, item })
            console.error('BIS submission failed for:', item, error)
          }
        }

        // Check if all succeeded
        const allSucceeded = bisResponses.every(r => r.success)

        if (allSucceeded && bisResponses.length > 0) {
          this.handleResponse(bisResponses[0].response) // Use first response for message
        } else {
          // Show error
          const errorResponse = {
            status: 'error',
            message: 'Failed to register for back in stock notifications',
          }
          this.handleResponse(errorResponse)
        }

        // Handle newsletter signup if checkbox is checked
        if (this.isNewsletterChecked()) {
          await this.sendNewsletterSignup()
        }

        return false
      }

      async sendBIS (productId, variantId) {
        // eslint-disable-next-line no-undef
        return BIS.create(this.email, variantId, productId)
      }

      handleResponse (response) {
        const responseEl = this.getEl('response')
        const { errors = null, message = null } = response
        const errorArray = errors
          ? Object.keys(errors).map(key => errors[key])
          : []

        if (!responseEl) return
        this.toggleResponse(false)

        responseEl.innerHTML = ''
        responseEl.dataset.status = response.status.toLowerCase()
        responseEl.innerHTML = message
          ? `<p>${message}</p>`
          : responseEl.innerHTML

        errorArray.forEach(error => {
          responseEl.innerHTML += `<p>${error}</p>`
        })

        this.toggleResponse(true)

        if (responseEl.dataset.status === 'ok') {
          this.toggleForm(false)
        }
      }

      toggleResponse (isVisible) {
        this.getEl('response').setAttribute(
          'aria-hidden',
          (!isVisible).toString(),
        )
      }

      toggleForm (isVisible) {
        this.getEl('form').setAttribute('aria-hidden', (!isVisible).toString())
      }

      // Newsletter signup methods
      isNewsletterChecked () {
        const newsletterEl = this.getEl('newsletter')
        return newsletterEl && newsletterEl.checked
      }

      async sendNewsletterSignup () {
        const ometriaFormId = this.getEl('newsletter').dataset.ometriaFormId
        if (!ometriaFormId) {
          console.warn('Ometria form ID not found')
          return
        }

        return await this.sendOmetriaForm(ometriaFormId)
      }

      async sendOmetriaForm (ometriaFormId) {
        const ometriaAccountId = this.getEl('newsletter').dataset
          .ometriaAccountId
        const formData = this.formatOmetriaFormData(
          ometriaFormId,
          ometriaAccountId,
        )

        const response = await fetch(
          `https://api.ometria.com/forms/signup/ajax`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body: formData,
          },
        )

        return response.json()
      }

      formatOmetriaFormData (ometriaFormId, ometriaAccountId) {
        const formData = {
          __form_id: ometriaFormId,
          ue: this.email,
          '@account': ometriaAccountId,
          '@return_url': '',
          '@subscription_status': 'SUBSCRIBED',
          country_id: this.getEl('newsletter').dataset.countryId,
          'properties.signup_source': 'back_in_stock',
        }

        // Convert to URL-encoded form data
        return Object.keys(formData)
          .map(
            key =>
              `${encodeURIComponent(key)}=${encodeURIComponent(formData[key])}`,
          )
          .join('&')
          .replace(/%20/g, '+')
      }

      hasEl (elementName) {
        return this.els[elementName] && this.els[elementName].exists
      }

      getEl (elementName) {
        return this.hasEl(elementName) ? this.els[elementName].element : null
      }

      getExternalEl (elementName) {
        return this.hasExternalEl(elementName)
          ? this.externalEls[elementName].element
          : null
      }

      get email () {
        const emailEl = this.getEl('email')
        return emailEl ? emailEl.value : ''
      }

      get productId () {
        return this._productId
      }

      set productId (productId) {
        this._productId = productId
      }

      get variantId () {
        return this._variantId
      }

      set variantId (variantId) {
        this._variantId = variantId
      }
    },
  )
}
